import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Scale, Loader2, Info, ChevronDown, ChevronUp, FileSpreadsheet, FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import { LIABILITY_TYPE_LABEL } from '../liabilities/liabilityConstants';
import FAQLink from '../faqs/FAQLink';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

function fmt(v, cur) {
  if (v == null) return '—';
  try {
    return `${cur ? cur + ' ' : ''}${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  } catch {
    return `${v}`;
  }
}

/**
 * Statement of Net Worth card — read-only summary.
 *
 * Shows assets + liabilities + net worth, grouped by currency. Detail is
 * presented inline (collapsible per currency) so the user can see every row
 * the eventual export will include. NO FX conversion — consistent with the
 * Tax Data Pack, Cashflow Calendar, and Current Tax Year Position cards.
 */
export default function NetWorthCard({ refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openCurrencies, setOpenCurrencies] = useState({});
  const [downloadingFmt, setDownloadingFmt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/net-worth/summary`, { headers: authHeaders() });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.detail || `Request failed (${resp.status})`);
      }
      setData(await resp.json());
    } catch (e) {
      setError(e.message || 'Failed to load Net Worth');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const toggle = (cur) => setOpenCurrencies((s) => ({ ...s, [cur]: !s[cur] }));

  const download = async (fmt) => {
    setDownloadingFmt(fmt);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/net-worth/export?fmt=${fmt}`, { headers: authHeaders() });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.detail || `Export failed (${resp.status})`);
      }
      const blob = await resp.blob();
      // Derive filename from Content-Disposition if available
      let filename = `statement-of-net-worth.${fmt}`;
      const cd = resp.headers.get('content-disposition') || '';
      const match = cd.match(/filename="([^"]+)"/);
      if (match) filename = match[1];
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(e.message || 'Failed to download');
    } finally {
      setDownloadingFmt(null);
    }
  };

  return (
    <Card className="portfolio-card" data-testid="net-worth-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Scale className="w-5 h-5 text-emerald-700 shrink-0" />
            <CardTitle className="text-base sm:text-xl font-serif">Statement of Net Worth</CardTitle>
            <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300 text-xs">
              By currency — no FX conversion
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button" size="sm" variant="outline"
              onClick={() => download('xlsx')}
              disabled={!data || (data?.net_worth_by_currency || []).length === 0 || downloadingFmt !== null}
              className="h-8 px-2.5 text-xs border-emerald-200 text-emerald-800 hover:bg-emerald-50"
              data-testid="net-worth-export-xlsx"
            >
              {downloadingFmt === 'xlsx'
                ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                : <FileSpreadsheet className="w-3.5 h-3.5 mr-1" />}
              Excel
            </Button>
            <Button
              type="button" size="sm" variant="outline"
              onClick={() => download('pdf')}
              disabled={!data || (data?.net_worth_by_currency || []).length === 0 || downloadingFmt !== null}
              className="h-8 px-2.5 text-xs border-emerald-200 text-emerald-800 hover:bg-emerald-50"
              data-testid="net-worth-export-pdf"
            >
              {downloadingFmt === 'pdf'
                ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                : <FileText className="w-3.5 h-3.5 mr-1" />}
              PDF
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-4">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : error ? (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="net-worth-error">
            {error}
          </div>
        ) : !data ? null : (data.net_worth_by_currency || []).length === 0 ? (
          <div className="flex items-start gap-2 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-3" data-testid="net-worth-empty">
            <Info className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" />
            <span>Add assets and liabilities to see your net worth position by currency.</span>
          </div>
        ) : (
          <>
            <div className="space-y-2" data-testid="net-worth-currencies">
              {data.net_worth_by_currency.map((row) => {
                const isOpen = openCurrencies[row.currency];
                const assets = data.assets_by_currency?.[row.currency] || [];
                const liabs = data.liabilities_by_currency?.[row.currency] || [];
                const positive = row.net_worth >= 0;
                return (
                  <div key={row.currency} className="border border-slate-200 rounded-md bg-white" data-testid={`net-worth-row-${row.currency}`}>
                    <button
                      type="button"
                      onClick={() => toggle(row.currency)}
                      className="w-full px-3 py-2.5 flex items-center justify-between hover:bg-slate-50 rounded-md"
                      data-testid={`net-worth-toggle-${row.currency}`}
                    >
                      <div className="flex items-center gap-2">
                        {isOpen ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                        <span className="text-sm font-semibold text-slate-900">{row.currency}</span>
                        <span className="text-[11px] text-slate-500">
                          {row.asset_count} {row.asset_count === 1 ? 'asset' : 'assets'} ·
                          {' '}{row.liability_count} {row.liability_count === 1 ? 'liability' : 'liabilities'}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 text-xs">
                        <span className="text-slate-600"><span className="text-slate-400">Assets</span> {fmt(row.total_assets, row.currency)}</span>
                        <span className="text-slate-600"><span className="text-slate-400">Liabilities</span> {fmt(row.total_liabilities, row.currency)}</span>
                        <span className={`font-semibold ${positive ? 'text-emerald-700' : 'text-red-600'}`}>
                          <span className="text-slate-400 font-normal mr-1">Net</span>{fmt(row.net_worth, row.currency)}
                        </span>
                      </div>
                    </button>

                    {isOpen && (
                      <div className="px-3 pb-3 pt-1 space-y-3" data-testid={`net-worth-detail-${row.currency}`}>
                        <div>
                          <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mb-1">Assets</div>
                          {assets.length === 0 ? (
                            <p className="text-xs text-slate-500">No assets in {row.currency}.</p>
                          ) : (
                            <div className="space-y-1">
                              {assets.map((a) => (
                                <div key={a.investment_id} className="text-xs flex justify-between gap-2 px-2.5 py-1.5 rounded border border-slate-100 bg-slate-50/40">
                                  <div className="min-w-0">
                                    <span className="font-medium text-slate-900">{a.name || '—'}</span>
                                    <span className="text-slate-500"> · {a.type}{a.counterparty ? ` · ${a.counterparty}` : ''}</span>
                                  </div>
                                  <div className="font-medium text-slate-900 shrink-0">
                                    {fmt(a.value, a.currency)}{a.value_is_estimated && <span className="text-slate-400"> (est.)</span>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <div>
                          <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mb-1">Liabilities</div>
                          {liabs.length === 0 ? (
                            <p className="text-xs text-slate-500">No liabilities in {row.currency}.</p>
                          ) : (
                            <div className="space-y-1">
                              {liabs.map((l) => (
                                <div key={l.liability_id} className="text-xs flex justify-between gap-2 px-2.5 py-1.5 rounded border border-slate-100 bg-slate-50/40">
                                  <div className="min-w-0">
                                    <span className="font-medium text-slate-900">{l.name || '—'}</span>
                                    <span className="text-slate-500">
                                      {' · '}{LIABILITY_TYPE_LABEL[l.liability_type] || l.liability_type}
                                      {l.counterparty ? ` · ${l.counterparty}` : ''}
                                    </span>
                                  </div>
                                  <div className="font-medium text-slate-900 shrink-0">
                                    {fmt(l.outstanding_balance, l.currency)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {(() => {
              const sketch = data.home_currency_net_worth;
              if (!sketch) return null;
              if (!sketch.available) {
                return (
                  <p className="mt-3 text-[11px] text-amber-700 italic" data-testid="net-worth-home-unavailable">
                    Home Currency Net Worth unavailable — the FX feed could not be reached. Per-currency rows above are unaffected.
                  </p>
                );
              }
              const positive = sketch.net_worth >= 0;
              return (
                <div
                  className="mt-3 rounded-md border border-emerald-200 bg-emerald-50/60 px-3 py-2.5"
                  data-testid="net-worth-home-currency"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-xs text-emerald-900">
                      <span className="font-semibold">Home Currency Net Worth ({sketch.home_currency})</span>
                      <span className="text-emerald-800/70"> · assets {fmt(sketch.total_assets, sketch.home_currency)} − liabilities {fmt(sketch.total_liabilities, sketch.home_currency)}</span>
                    </div>
                    <div className={`text-sm font-semibold ${positive ? 'text-emerald-800' : 'text-red-700'}`}>
                      {fmt(sketch.net_worth, sketch.home_currency)}
                    </div>
                  </div>
                  <p className="mt-1 text-[11px] text-emerald-900/70 italic">
                    Indicative figure — converted via exchangerate.host live rates
                    {sketch.fetched_at ? ` (${String(sketch.fetched_at).slice(0, 19).replace('T', ' ')} UTC)` : ''}.
                    Use the per-currency rows above for filing or audit.
                    {sketch.unconverted_currencies?.length ? ` Excluded (no FX rate available): ${sketch.unconverted_currencies.join(', ')}.` : ''}
                  </p>
                </div>
              );
            })()}
            <p className="mt-3 text-[11px] text-slate-500 italic">
              Native-currency view — banks and accountants apply their own FX rates. Closed and related-party holdings are excluded.
            </p>
            <div className="mt-2">
              <FAQLink categoryKey="net-worth" />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
